import DecaService from '@/services/deca'

const pad = n => String(n).padStart(2, '0')
const fechaLocal = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const ayer = () => {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return fechaLocal(d)
}

// Vehículo con el que se trabaja, tal como lo guarda Configuración. OJO: la
// matrícula de `Vehiculo` es la de la cisterna (comprobante); para el DeCA solo
// valen los ids, el servidor resuelve las matrículas reales.
const idsDe = cfg => ({
  vehiculo_id: cfg?.Vehiculo?.id ?? null,
  cisterna_id: cfg?.Cisterna?.id ?? null,
  remolque_id: cfg?.Remolque?.id ?? null,
})
const mismosIds = (a, b) => !!a && !!b && ['vehiculo_id', 'cisterna_id', 'remolque_id'].every(k => (a[k] ?? null) === (b[k] ?? null))

/**
 * DeCA de la jornada por ruta: { [rutaId]: { requerido, fecha, deca, ids } }.
 * `ids` solo existe cuando el conductor pulsó «Iniciar jornada» en este móvil:
 * un DeCA emitido por la oficina también hay que confirmarlo con el vehículo real.
 */
export default {
  namespaced: true,
  state: {
    jornadas: {},
  },
  getters: {
    actual: (state, getters, rootState, rootGetters) => {
      const cfg = rootGetters['recogida/filterConfigRutaActual']
      const rutaId = cfg?.Ruta?.id ?? null
      const nada = { rutaId, requerido: false, bloquea: false, estado: 'no-requerido', deca: null, ids: idsDe(cfg) }
      // Módulo no contratado o apagado en la instalación (GET /config): no hay DeCA.
      if (!rutaId || !rootGetters['config/tiene']('deca')) return nada

      const entrada = state.jornadas[rutaId]
      const ruta = (rootGetters['sync/rutas'] || []).find(r => r.Ruta?.id === rutaId)
      const requerido = typeof entrada?.requerido === 'boolean' ? entrada.requerido : !!ruta?.Deca?.requerido
      if (!requerido) return nada

      // Una jornada que cruza la medianoche sigue valiendo mientras haya movimientos sin enviar.
      const enFecha = !!entrada?.deca && (
        entrada.fecha === fechaLocal()
        || (entrada.fecha === ayer() && rootGetters['recogida/pendingCount'] > 0)
      )
      const deca = enFecha ? entrada.deca : null
      const ids = idsDe(cfg)

      let estado = 'ok'
      if (!deca || !entrada.ids) estado = 'sin-jornada'
      else if (deca.estado === 'generado' && !mismosIds(entrada.ids, ids)) estado = 'cambio-vehiculo'

      return { rutaId, requerido: true, bloquea: estado !== 'ok', estado, deca, ids }
    },
  },
  mutations: {
    SET_JORNADA(state, { rutaId, datos }) {
      const previa = state.jornadas[rutaId] || {}
      const mismaFecha = !datos.fecha || previa.fecha === datos.fecha
      state.jornadas = { ...state.jornadas, [rutaId]: { ...(mismaFecha ? previa : {}), ...datos } }
    },
    PURGAR(state) {
      const limite = ayer()
      state.jornadas = Object.fromEntries(
        Object.entries(state.jornadas).map(([id, j]) => [
          id,
          j.fecha && j.fecha < limite ? { requerido: j.requerido } : j,
        ])
      )
    },
  },
  actions: {
    /** Consulta silenciosa al entrar en la ruta: ¿lleva DeCA? ¿hay ya uno (oficina, cierre)? */
    async refrescar({ commit, dispatch, state, getters, rootState }) {
      const rutaId = getters.actual.rutaId
      if (!rutaId || !rootState.online) return
      commit('PURGAR')
      const previa = state.jornadas[rutaId]
      const fecha = getters.actual.deca ? previa.fecha : fechaLocal()
      const { requerido, deca } = await DecaService.consultar(rutaId, fecha)
      commit('SET_JORNADA', { rutaId, datos: deca ? { requerido, fecha, deca } : { requerido } })
      if (deca) await dispatch('guardarPdf', deca)
    },

    /** «Iniciar jornada»: el servidor emite el DeCA con el vehículo de Configuración. */
    async iniciar({ commit, dispatch, getters }) {
      const { rutaId, ids } = getters.actual
      const fecha = fechaLocal()
      const payload = { ruta_id: rutaId, fecha, remolque_id: ids.remolque_id }
      if (ids.vehiculo_id) payload.vehiculo_id = ids.vehiculo_id
      if (ids.cisterna_id) payload.cisterna_id = ids.cisterna_id

      const { requerido, deca } = await DecaService.iniciarJornada(payload)
      commit('SET_JORNADA', { rutaId, datos: deca ? { requerido, fecha, deca, ids } : { requerido } })
      if (deca) await dispatch('guardarPdf', deca)
      return deca
    },

    /** Tras enviar datos: la descarga cierra el DeCA en el servidor; traemos la versión de cierre. */
    async refrescarAbiertos({ commit, dispatch, state }) {
      for (const [rutaId, j] of Object.entries(state.jornadas)) {
        if (!j.deca || j.deca.estado !== 'generado') continue
        const { requerido, deca } = await DecaService.consultar(rutaId, j.fecha)
        if (deca) {
          commit('SET_JORNADA', { rutaId, datos: { requerido, fecha: j.fecha, deca } })
          await dispatch('guardarPdf', deca)
        }
      }
    },

    // El PDF es la copia del conductor para enseñar sin red; si la descarga falla
    // el QR (que es lo que la norma exige como mínimo) sigue disponible.
    async guardarPdf({ state }, deca) {
      try {
        if (!(await DecaService.pdf(deca.id))) await DecaService.descargarPdf(deca.id)
        await DecaService.purgar(Object.values(state.jornadas).map(j => j.deca?.id).filter(Boolean))
      } catch (e) {
        console.warn('DeCA: no se pudo guardar el PDF', e)
      }
    },
  },
}
